import React, { useMemo } from "react";
import { Calendar, Flag, CheckCircle } from "lucide-react";

/**
 * TripProgressBar
 * Props:
 *  - dateDepart (string "YYYY-MM-DD")
 *  - dateRetour (string "YYYY-MM-DD")
 *  - totalDays (number)
 *  - currentDayIndex (number | null)  — 0-based index of today in the itinerary
 */
export function useTripProgress(dateDepart, dateRetour) {
  return useMemo(() => {
    if (!dateDepart || !dateRetour) return { state: "unknown", pct: 0, jMinus: null, currentDayIndex: null };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const depart = new Date(dateDepart);
    depart.setHours(0, 0, 0, 0);

    const retour = new Date(dateRetour);
    retour.setHours(23, 59, 59, 999);

    const retourMidnight = new Date(dateRetour);
    retourMidnight.setHours(0, 0, 0, 0);

    const totalDays = Math.max(1, Math.round((retourMidnight - depart) / 86400000) + 1);

    if (today < depart) {
      const jMinus = Math.round((depart - today) / 86400000);
      return { state: "before", pct: 0, jMinus, currentDayIndex: null, totalDays };
    }

    if (today > retourMidnight) {
      return { state: "after", pct: 100, jMinus: null, currentDayIndex: null, totalDays };
    }

    const dayIndex = Math.round((today - depart) / 86400000); // 0-based
    const pct = Math.min(100, Math.round(((dayIndex + 1) / totalDays) * 100));
    return { state: "during", pct, jMinus: null, currentDayIndex: dayIndex, totalDays };
  }, [dateDepart, dateRetour]);
}

export function TripProgressBar({ dateDepart, dateRetour }) {
  const { state, pct, jMinus, currentDayIndex, totalDays } = useTripProgress(dateDepart, dateRetour);

  if (state === "unknown") return null;

  if (state === "before") {
    return (
      <div
        className="flex items-center gap-4 p-4 bg-[#F9F6F0] border border-[#DCD4C6]"
        style={{ borderRadius: "2px" }}
        data-testid="trip-progress-before"
      >
        <div className="w-10 h-10 flex items-center justify-center bg-[#C17F59]/10 border border-[#C17F59]/30 flex-shrink-0" style={{ borderRadius: "50%" }}>
          <Calendar size={16} strokeWidth={1.5} className="text-[#C17F59]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 mb-1.5">
            <span className="text-2xl font-semibold text-[#C17F59]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              J — {jMinus}
            </span>
            <span className="text-sm text-[#9A8F86] italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              avant le départ
            </span>
          </div>
          <div className="w-full h-1.5 bg-[#DCD4C6] rounded-full overflow-hidden">
            <div className="h-full w-0 bg-[#C17F59]" />
          </div>
          <p className="text-xs text-[#9A8F86] mt-1 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            Départ le {new Date(dateDepart).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>
      </div>
    );
  }

  if (state === "after") {
    return (
      <div
        className="flex items-center gap-4 p-4 bg-emerald-50 border border-emerald-200"
        style={{ borderRadius: "2px" }}
        data-testid="trip-progress-after"
      >
        <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 border border-emerald-300 flex-shrink-0" style={{ borderRadius: "50%" }}>
          <CheckCircle size={16} strokeWidth={1.5} className="text-emerald-600" />
        </div>
        <div>
          <p className="text-base font-medium text-emerald-800" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}>
            Voyage terminé — Souvenirs gravés
          </p>
          <p className="text-xs text-emerald-600 italic mt-0.5" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            {totalDays} jour{totalDays > 1 ? "s" : ""} de voyage · retour le {new Date(dateRetour).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
      </div>
    );
  }

  // state === "during"
  return (
    <div
      className="p-4 bg-[#FDF8F3] border border-[#C17F59]/30"
      style={{ borderRadius: "2px" }}
      data-testid="trip-progress-during"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Flag size={14} strokeWidth={1.5} className="text-[#C17F59]" />
          <span className="text-sm font-medium text-[#2B2623]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}>
            Jour {currentDayIndex + 1} sur {totalDays}
          </span>
        </div>
        <span className="text-sm text-[#C17F59] font-semibold" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          {pct}%
        </span>
      </div>
      <div className="w-full h-2.5 bg-[#DCD4C6] rounded-full overflow-hidden">
        <div
          className="h-full bg-[#C17F59] transition-all duration-700"
          style={{ width: `${pct}%`, borderRadius: "inherit" }}
        />
      </div>
      <p className="text-xs text-[#9A8F86] mt-1.5 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
        Retour le {new Date(dateRetour).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
      </p>
    </div>
  );
}
